import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellOff, Check, Play } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, Card, EmptyState } from "@/components/common/Primitives";
import { StatusBadge } from "@/components/common/StatusBadge";

export default function RemindersPage() {
  const { can } = useAuth();
  const [rems, setRems] = useState(null);
  const [notes, setNotes] = useState(null);
  const load = useCallback(() => {
    api.get("/reminders").then((r) => setRems(r.data));
    api.get("/notifications").then((r) => setNotes(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const snooze = async (key) => { await api.post(`/reminders/${encodeURIComponent(key)}/snooze`, { days: 3 }); toast.success("Snoozed for 3 days"); load(); };
  const ack = async (key) => { await api.post(`/reminders/${encodeURIComponent(key)}/ack`, {}); toast.success("Acknowledged — will resurface if it escalates"); load(); };
  const dispatch = async () => {
    try { const { data } = await api.post("/jobs/reminder-dispatch"); const r = data.results[0]; toast.success(r?.skipped ? `Skipped: ${r.skipped}` : `Dispatched ${r?.notifications_created ?? 0} notification(s)`); load(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <div>
      <PageHeader title="Reminders" subtitle="Derived live from expiry dates, service schedules, consumable wear and pending challans" testId="reminders-header"
        actions={can("admin") && <><button onClick={dispatch} data-testid="run-dispatch-btn" className="flex h-10 items-center gap-1.5 rounded-full border px-3 text-sm font-medium text-primary hover:bg-primary/10"><Play className="h-4 w-4" />Run dispatch</button><button onClick={async () => { try { const { data } = await api.post("/jobs/daily-digest"); const r = data.results[0]; toast.success(r?.skipped ? "Skipped — commissioning" : r?.digest || `Digest sent to ${r?.digests_sent} user(s)`); load(); } catch (e) { toast.error(errorMessage(e)); } }} data-testid="run-digest-btn" className="flex h-10 items-center gap-1.5 rounded-full border px-3 text-sm font-medium text-primary hover:bg-primary/10"><Play className="h-4 w-4" />Send digest</button></>} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <Card title="Needs attention" className="lg:col-span-3" testId="reminders-list">
          {!rems ? <Spinner /> : rems.length === 0 ? <EmptyState title="All clear" description="No reminders pending. Snoozed or acknowledged items are hidden." /> : (
            <ul className="divide-y">
              {rems.map((r) => (
                <li key={r.key} className="flex flex-wrap items-center gap-2 py-3" data-testid={`reminder-${r.key}`}>
                  <StatusBadge status={r.severity} />
                  <div className="min-w-0 flex-1">
                    <Link to={`/vehicles/${r.vehicle_id}`} className="block truncate text-sm font-medium hover:underline">{r.message}</Link>
                    <p className="text-[11px] text-muted-foreground">Stage {r.stage} of 5 · {r.source_type}</p>
                  </div>
                  <button onClick={() => snooze(r.key)} data-testid={`snooze-${r.key}`} className="flex h-8 items-center gap-1 rounded-lg px-2 text-xs font-medium text-primary hover:bg-primary/10"><BellOff className="h-3.5 w-3.5" />Snooze</button>
                  <button onClick={() => ack(r.key)} data-testid={`ack-${r.key}`} className="flex h-8 items-center gap-1 rounded-lg px-2 text-xs font-medium text-primary hover:bg-primary/10"><Check className="h-3.5 w-3.5" />Acknowledge</button>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Inbox" className="lg:col-span-2" testId="notifications-list" action={notes?.unread > 0 && <button onClick={readAll} data-testid="mark-all-read-btn" className="text-sm font-medium text-primary hover:underline">Mark all read</button>}>
          {!notes ? <Spinner /> : notes.items.length === 0 ? <p className="text-sm text-muted-foreground">No notifications yet. They are created by the dispatch job once commissioning mode is off.</p> : (
            <ul className="space-y-3">
              {notes.items.slice(0, 30).map((n) => (
                <li key={n.id} className={n.read_at ? "opacity-60" : ""} data-testid={`notification-${n.id}`}>
                  <p className="text-sm font-medium">{n.title}</p>
                  <p className="text-xs text-muted-foreground">{n.message}</p>
                  <p className="text-[11px] text-muted-foreground">{fmtDateTime(n.created_at)}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
